// Data contract shared by the local provider and a future server aggregate provider.
export const USAGE_BUCKETS = [
  {
    label: "0시간 이상 ~ 20,000시간 미만",
    upperHours: 20000,
    color: "#0066FF",
    textColor: "#FFFFFF"
  },
  {
    label: "20,000시간 이상 ~ 30,000시간 미만",
    upperHours: 30000,
    color: "#26C6DA"
  },
  {
    label: "30,000시간 이상 ~ 40,000시간 미만",
    upperHours: 40000,
    color: "#FFD54F"
  },
  {
    label: "40,000시간 이상 ~ 50,000시간 미만",
    upperHours: 50000,
    color: "#FF8A65",
    textColor: "#FFFFFF"
  },
  {
    label: "50,000시간 이상",
    upperHours: Infinity,
    color: "#FF3333",
    textColor: "#FFFFFF"
  }
];
// Default label color; darker buckets can override it in USAGE_BUCKETS.
export const USAGE_BUCKET_TEXT_COLOR = "#111111";
export const UNKNOWN_BUCKET = { label: "사용시간 미확인", color: "#d4d4d4" };
export const REVIEW_HOURS = 50000;
export const STATISTICS_PAGE_SIZE = 20;
const clean = (value) => String(value ?? "").trim();

export function usageSeconds(value) {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

export function bucketIndex(seconds) {
  if (seconds === null) return -1;
  return USAGE_BUCKETS.findIndex(
    (bucket) => seconds < bucket.upperHours * 3600
  );
}

export function normalizeDevice(device) {
  const region = clean(device.school_region);
  const schoolId = clean(device.school_id ?? device.school_idx);
  const schoolName = clean(device.school_name);
  const hasId = schoolId !== "" && schoolId !== "0";
  // Composite grouping keys are not invented school IDs. Name-only groups remain
  // explicitly distinguishable from real IDs; ambiguous records are never guessed.
  const schoolKey = JSON.stringify(
    hasId ? ["id", schoolId] : ["name", region, schoolName]
  );
  const uuid = clean(device.device_uuid);
  const location = clean(device.app_location);
  return {
    regionKey: JSON.stringify([region]),
    regionLabel: region || "지역 미지정",
    schoolKey,
    schoolLabel: schoolName || (hasId ? "학교명 미지정" : "학교 미지정"),
    schoolId: hasId ? schoolId : null,
    nameOnly: !hasId && !!schoolName,
    deviceLabel: location || "위치 미지정",
    uuid,
    seconds: usageSeconds(device.total_usage_sec)
  };
}

function aggregate(records, keyField, labelField) {
  const groups = new Map();
  for (const record of records) {
    const key = record[keyField];
    if (!groups.has(key))
      groups.set(key, {
        key,
        label: record[labelField],
        schoolId: record.schoolId,
        nameOnly: record.nameOnly,
        total: 0,
        counts: Array(USAGE_BUCKETS.length).fill(0),
        unknown: 0
      });
    const group = groups.get(key);
    group.total += 1;
    const index = bucketIndex(record.seconds);
    if (index < 0) group.unknown += 1;
    else group.counts[index] += 1;
  }
  return [...groups.values()].sort((a, b) =>
    a.label.localeCompare(b.label, "ko", { numeric: true })
  );
}

export function aggregateRegions(records) {
  return aggregate(records, "regionKey", "regionLabel");
}
export function aggregateSchools(records, regionKey) {
  return aggregate(
    records.filter((record) => record.regionKey === regionKey),
    "schoolKey",
    "schoolLabel"
  );
}
export function schoolDevices(records, regionKey, schoolKey) {
  return records
    .filter(
      (record) =>
        record.regionKey === regionKey && record.schoolKey === schoolKey
    )
    .sort(
      (a, b) =>
        (b.seconds ?? -1) - (a.seconds ?? -1) || a.uuid.localeCompare(b.uuid)
    );
}
export function paginate(items, page, size = STATISTICS_PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(items.length / size));
  const currentPage = Math.max(1, Math.min(page, totalPages));
  return {
    items: items.slice((currentPage - 1) * size, currentPage * size),
    total: items.length,
    totalPages,
    page: currentPage
  };
}

// Compute from the entire selected school, before slicing the current page.
export function schoolAxisMax(records) {
  const maximum = records.reduce(
    (max, record) => Math.max(max, (record.seconds ?? 0) / 3600),
    REVIEW_HOURS
  );
  return Math.ceil((maximum * 1.1) / 10000) * 10000;
}

// Replacing this provider with server responses preserves the modal's view models.
export function createLocalStatisticsProvider(devices) {
  const records = devices.map(normalizeDevice);
  return {
    regions: () => aggregateRegions(records),
    schools: (regionKey) => aggregateSchools(records, regionKey),
    devices: (regionKey, schoolKey) =>
      schoolDevices(records, regionKey, schoolKey)
  };
}

export function formatUsage(seconds) {
  if (seconds === null) return "사용시간 미확인";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = Math.floor(seconds % 60);
  return `${hours.toLocaleString("ko-KR")}시간${
    minutes ? ` ${minutes}분` : ""
  }${remainder ? ` ${remainder}초` : ""}`;
}
